// src/utils/reportApi.js

/**
 * API utilities for fetching saved reports from returnReport endpoint.
 */

const BASE_URL = 'https://europe-west3-match-medic-p0.cloudfunctions.net';

/**
 * Fetch a report from the returnReport API.
 * 
 * @param {object} params - Request parameters
 * @param {string} params.requestId - The report request ID (e.g., "req_1773103717530_0n1h5r7")
 * @param {string} params.password - The password for the report
 * @param {string} [params.reportType='analysis'] - 'analysis' or 'generation'
 * @param {boolean} [params.includeLinked=true] - Whether to fetch linked report data
 * @returns {Promise<object>} The report data
 * @throws {Error} With status code and message on failure
 */
export async function fetchReport({
  requestId,
  password,
  reportType = 'analysis',
  includeLinked = true,
}) {
  if (!requestId) {
    throw new Error('Missing requestId');
  }
  if (!password) {
    throw new Error('Missing password');
  }
  if (reportType !== 'analysis' && reportType !== 'generation') {
    throw new Error('Invalid reportType. Must be "analysis" or "generation"');
  }

  const url = `${BASE_URL}/returnReport/report/${reportType}/${encodeURIComponent(requestId)}/${encodeURIComponent(password)}${includeLinked ? '?includeLinked=true' : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || data.operation || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

/**
 * Parse error from fetchReport and return user-friendly message.
 * 
 * @param {Error} error - The error from fetchReport
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  if (error.status === 403) {
    return 'Invalid password. Please check and try again.';
  }
  if (error.status === 404) {
    return 'Report not found. The link may be expired or invalid.';
  }
  if (error.status === 400) {
    return 'Invalid request. Please check the report link.';
  }
  if (error.status >= 500) {
    return 'Server error. Please try again later.';
  }
  return error.message || 'An unexpected error occurred.';
}

/**
 * Extract analysis data from report response.
 * Handles various payload structures.
 * 
 * @param {object} report - The report data from fetchReport
 * @returns {object|null} The analysis object or null if not found
 */
export function extractAnalysisFromReport(report) {
  if (!report || typeof report !== 'object') return null;
  
  // The API wraps the response in an 'operation' object
  const source = report.operation || report;
  
  // Direct analysis field
  if (source.analysis && typeof source.analysis === 'object') {
    return source.analysis;
  }
  
  // Nested paths (fallbacks)
  const paths = [
    report?.data?.analysis,
    report?.result?.analysis,
  ];
  
  for (const candidate of paths) {
    if (candidate && typeof candidate === 'object' && candidate.baldness_bucket) {
      return candidate;
    }
  }
  
  return null;
}

/**
 * Extract metadata from report response.
 * 
 * @param {object} report - The report data from fetchReport
 * @returns {object} The reportMetadata object (may be empty)
 */
export function extractMetadataFromReport(report) {
  // The API wraps the response in an 'operation' object
  const source = report?.operation || report;
  return source?.reportMetadata || {};
}

/**
 * Extract image URLs from report response.
 * 
 * @param {object} report - The report data from fetchReport
 * @returns {object} Object with originalImageUrl and generatedImageUrl (if available)
 */
export function extractImagesFromReport(report) {
  const images = {
    originalImageUrl: null,
    generatedImageUrl: null,
  };
  
  // The API wraps the response in an 'operation' object
  const source = report?.operation || report;
  
  // Debug: Log the source we're extracting from
  console.log('[reportApi] extractImagesFromReport - source:', source);
  
  // Original image URL (from analysis or generation)
  if (source?.originalImageUrl) {
    images.originalImageUrl = source.originalImageUrl;
    console.log('[reportApi] Found originalImageUrl:', source.originalImageUrl);
  }
  
  // Generated image URL (from linkedGeneration or direct generation report)
  if (source?.linkedGeneration?.output?.firebaseStorageUrl) {
    images.generatedImageUrl = source.linkedGeneration.output.firebaseStorageUrl;
    console.log('[reportApi] Found generatedImageUrl in linkedGeneration');
  } else if (source?.output?.firebaseStorageUrl) {
    images.generatedImageUrl = source.output.firebaseStorageUrl;
    console.log('[reportApi] Found generatedImageUrl at output');
  }
  
  console.log('[reportApi] Final extracted images:', images);
  return images;
}
